import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Mic, MicOff, Send, Zap, Play } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXAMPLE_COMMANDS = [
  "What can you do?",
  "Cybersecurity on LinkedIn",
  "TikTok fashion videos"
];

const API_PARTNERS = [
  { name: "TikTok" },
  { name: "Instagram" },
  { name: "LinkedIn" },
  { name: "Twitter" },
  { name: "YouTube" },
  { name: "Meta" }
];

/**
 * HeroSection Component - Top mic works directly (record on click)
 */
export default function HeroSection({ 
  selectedLanguage,
  onAIResponse,
  onShowVideo,
  onOpenVoiceChat
}) {
  const [textInput, setTextInput] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const mimeTypeRef = useRef('audio/webm');

  const processTextCommand = async (text = textInput) => {
    if (!text.trim()) return;
    setIsProcessing(true);
    onAIResponse("", true);
    
    try {
      const response = await fetch(`${API}/text/command/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: text, language: selectedLanguage })
      });
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedResponse = "";
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === 'chunk') {
                accumulatedResponse += data.content;
                onAIResponse(accumulatedResponse, true);
              } else if (data.type === 'done') {
                onAIResponse(accumulatedResponse, false, data.suggested_campaign);
              }
            } catch (parseError) {
              // JSON parse errors expected for incomplete chunks - ignore
            }
          }
        }
      }
      
      if (accumulatedResponse) onAIResponse(accumulatedResponse, false);
      setTextInput("");
    } catch (error) {
      toast.error("Failed to connect. Please try again.");
      onAIResponse("Sorry, I'm having trouble connecting. Please try again.", false);
    } finally {
      setIsProcessing(false);
    }
  };

  // Direct voice recording from the top mic button
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : 'audio/mp4';
      
      mimeTypeRef.current = mimeType;
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      
      mediaRecorderRef.current.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeTypeRef.current });
        
        if (audioBlob.size > 0) {
          setIsProcessing(true);
          onAIResponse("", true);
          toast.info("Processing your voice...");
          
          try {
            const formData = new FormData();
            const fileExtension = mimeTypeRef.current.includes('mp4') ? 'mp4' : 'webm';
            formData.append('audio', audioBlob, `recording.${fileExtension}`);
            formData.append('language', selectedLanguage || 'en');
            
            const response = await fetch(`${API}/voice/command-file`, {
              method: 'POST',
              body: formData
            });
            
            if (!response.ok) {
              const error = await response.json();
              throw new Error(error.detail || 'Voice processing failed');
            }
            
            const data = await response.json();
            
            if (data.transcribed_text) {
              toast.success(`Heard: "${data.transcribed_text}"`);
              setTextInput(data.transcribed_text);
            }
            
            if (data.response) {
              onAIResponse(data.response, false, data.suggested_campaign);
            } else if (data.transcribed_text) {
              // If no direct response, process the transcribed text
              await processTextCommand(data.transcribed_text);
            } else {
              onAIResponse("I couldn't understand that. Please try again.", false);
            }
          } catch (error) {
            console.error("Voice error:", error);
            toast.error("Voice processing failed. Please try again.");
            onAIResponse("Voice processing failed. Please try typing instead.", false);
          } finally {
            setIsProcessing(false);
          }
        }
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      toast.success("🎤 Recording... Tap again to stop");
    } catch (error) {
      console.error("Mic error:", error);
      toast.error("Could not access microphone. Please check permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  return (
    <section className="relative pt-24 sm:pt-32 pb-12 sm:pb-20 px-4 sm:px-6 lg:px-8 min-h-screen flex items-center">
      <div className="fixed inset-0 hero-gradient pointer-events-none z-0" />
      
      <div className="max-w-7xl mx-auto w-full relative">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left - Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center lg:text-left"
          >
            <Badge className="mb-4 sm:mb-6 bg-primary/10 text-primary border-primary/20 px-3 sm:px-4 py-1 text-xs sm:text-sm" data-testid="hero-badge">
              <Zap className="w-3 h-3 mr-1 sm:mr-2" />
              Powered by GPT-5.2 & Sora 2
            </Badge>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter leading-tight mb-4 sm:mb-6" data-testid="hero-title">
              Your AI Marketing Team, <span className="gradient-text">Voice Activated</span>
            </h1>
            
            <p className="text-base sm:text-lg text-muted-foreground mb-6 sm:mb-8 max-w-xl mx-auto lg:mx-0" data-testid="hero-subtitle">
              Launch complete marketing campaigns in any language with just your 
              voice. From strategy to content to publishing — fully automated.
            </p>

            {/* Stats */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-6 sm:gap-8 mb-8 sm:mb-10">
              <div className="text-center lg:text-left">
                <p className="text-2xl sm:text-3xl font-bold text-primary">85%</p>
                <p className="text-xs sm:text-sm text-muted-foreground">Time Saved</p>
              </div>
              <div className="text-center lg:text-left">
                <p className="text-2xl sm:text-3xl font-bold text-primary">10x</p>
                <p className="text-xs sm:text-sm text-muted-foreground">Content Output</p>
              </div>
              <div className="text-center lg:text-left">
                <p className="text-2xl sm:text-3xl font-bold text-primary">12</p>
                <p className="text-xs sm:text-sm text-muted-foreground">Languages</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row flex-wrap justify-center lg:justify-start gap-3 sm:gap-4">
              {/* Try Voice Demo - Opens voice chat modal */}
              <Button 
                className="w-full sm:w-auto rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-6 sm:px-8 neon-glow"
                onClick={onOpenVoiceChat}
                data-testid="try-demo-btn"
              >
                <Mic className="w-5 h-5 mr-2" />
                Try Voice Demo
              </Button>
              <Button 
                variant="outline" 
                className="w-full sm:w-auto rounded-full px-8"
                onClick={onShowVideo} 
                data-testid="hero-watch-btn"
              >
                <Play className="w-5 h-5 mr-2" />
                Watch How It Works
              </Button>
            </div>
          </motion.div>

          {/* Right - Interactive Demo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative mt-8 lg:mt-0"
          >
            <div className="glass-card rounded-2xl sm:rounded-3xl p-6 sm:p-8 relative overflow-hidden" data-testid="voice-demo">
              <div className="absolute top-0 right-0 w-40 h-40 bg-primary/20 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-blue-500/20 rounded-full blur-3xl" />
              
              <div className="relative">
                <p className="text-sm text-muted-foreground mb-4 text-center">Try it now — speak or type</p>

                {/* Voice Orb - Direct recording on click */}
                <div className="flex justify-center mb-6" id="demo">
                  <button
                    onClick={isRecording ? stopRecording : startRecording}
                    disabled={isProcessing}
                    className={`
                      relative w-24 h-24 rounded-full flex items-center justify-center cursor-pointer transition-all duration-300
                      ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}
                      ${isRecording 
                        ? 'bg-green-500/30 animate-pulse scale-110' 
                        : 'bg-gradient-to-br from-primary/30 to-blue-600/30 hover:from-primary/40 hover:to-blue-600/40 hover:scale-105'
                      }
                      active:scale-95
                    `}
                    style={{ 
                      border: isRecording ? '3px solid rgba(34, 197, 94, 0.8)' : '2px solid rgba(0, 229, 255, 0.5)',
                      backdropFilter: 'blur(10px)',
                      boxShadow: isRecording 
                        ? '0 0 40px rgba(34, 197, 94, 0.6), 0 0 80px rgba(34, 197, 94, 0.3)' 
                        : '0 0 30px rgba(0, 229, 255, 0.3)'
                    }}
                    data-testid="voice-orb"
                  >
                    {isRecording ? (
                      <MicOff className="w-8 sm:w-10 h-8 sm:h-10 text-green-400 drop-shadow-[0_0_10px_rgba(34,197,94,0.8)]" />
                    ) : (
                      <Mic className="w-8 sm:w-10 h-8 sm:h-10 text-white drop-shadow-[0_0_10px_rgba(0,229,255,0.8)]" />
                    )}
                  </button>
                </div>
                
                {/* Recording status indicator */}
                {isRecording && (
                  <motion.p 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center text-green-400 text-sm mb-4 font-medium"
                  >
                    🔴 Recording... Click to stop and send
                  </motion.p>
                )}

                {/* Text Input */}
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Type your marketing command..."
                    className="flex-1 h-11 sm:h-12 bg-white/5 border border-white/10 rounded-full px-4 sm:px-5 text-sm sm:text-base focus:border-primary focus:outline-none"
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && processTextCommand()}
                    disabled={isProcessing}
                    data-testid="text-command-input"
                  />
                  <Button 
                    onClick={() => processTextCommand()}
                    disabled={!textInput.trim() || isProcessing}
                    className="h-11 w-11 sm:h-12 sm:w-12 rounded-full bg-primary text-primary-foreground flex-shrink-0"
                    data-testid="send-command-btn"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>

                {/* Example Commands */}
                <div className="mt-3 sm:mt-4 flex flex-wrap gap-2 justify-center">
                  {EXAMPLE_COMMANDS.map((cmd) => (
                    <button
                      key={cmd}
                      onClick={() => { setTextInput(cmd); processTextCommand(cmd); }}
                      disabled={isProcessing}
                      className="text-xs px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 transition-colors active:scale-95 disabled:opacity-50"
                    >
                      {cmd}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Official API Partners */}
            <div className="mt-6 text-center">
              <p className="text-xs text-muted-foreground mb-3">Official API Partners</p>
              <div className="flex justify-center gap-6 opacity-50">
                {API_PARTNERS.map((partner) => (
                  <span key={partner.name} className="text-xs font-medium">{partner.name}</span>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
